import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    ButtonGroup,
    Alert,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    startOfDay,
    endOfDay,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfYear,
    endOfYear,
    differenceInYears,
    isValid,
    format
} from 'date-fns';
import { Search, People, TrendingUp, AttachMoney, ContentCut } from '@mui/icons-material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import FeatureGuard from '../common/FeatureGuard';
import apiService from '../../services/api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { StatCard, Loading } from '../common';

const SalesByStaff = () => {
    const [filterType, setFilterType] = useState('month');
    const [dateRange, setDateRange] = useState({
        from: startOfMonth(new Date()),
        to: endOfDay(new Date())
    });
    const [customRange, setCustomRange] = useState({
        from: startOfMonth(new Date()),
        to: endOfDay(new Date())
    });
    const [error, setError] = useState('');
    const [staffData, setStaffData] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState('');
    const [loading, setLoading] = useState(false);
    const { formatPrice } = useCurrency();

    // Initial Fetch
    useEffect(() => {
        fetchStaffList();
        fetchStaffSales();
    }, []);

    const fetchStaffList = async () => {
        try {
            // Fetch all users/staff from admin endpoint
            const response = await apiService.get('/admin/users');
            if (response.success) {
                // Get all active staff members (exclude inactive users)
                const allStaff = (response.data || []).filter(user => 
                    user.status === 'active' && 
                    user.role_name !== 'Super Admin' // Exclude super admin from staff list
                );
                setStaffList(allStaff);
            }
        } catch (err) {
            console.error('Failed to fetch staff list', err);
            // Fallback: try to fetch stylists if admin/users fails
            try {
                const stylistResponse = await apiService.get('/stylists');
                if (stylistResponse.success) {
                    const stylistList = Array.isArray(stylistResponse.data)
                        ? stylistResponse.data
                        : (stylistResponse.data?.stylists || []);
                    setStaffList(stylistList);
                }
            } catch (fallbackErr) {
                console.error('Failed to fetch stylists as fallback', fallbackErr);
            }
        }
    };

    const fetchStaffSales = async () => {
        setLoading(true);
        try {
            const startDate = format(dateRange.from, 'yyyy-MM-dd');
            const endDate = format(dateRange.to, 'yyyy-MM-dd');

            let url = `/reports/staff?startDate=${startDate}&endDate=${endDate}`;
            if (selectedStaff) {
                url += `&staff_id=${selectedStaff}`;
            }

            const response = await apiService.get(url);
            if (response.success) {
                setStaffData(response.data.staffSales || []);
            } else {
                setError('Failed to fetch staff sales data');
            }
        } catch (err) {
            setError('Error fetching data: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Handle filter buttons
    const handleFilterChange = (type) => {
        setFilterType(type);
        setError('');

        const now = new Date();
        let newRange = { from: now, to: now };

        switch (type) {
            case 'today':
                newRange = { from: startOfDay(now), to: endOfDay(now) };
                break;
            case 'week':
                newRange = { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfDay(now) };
                break;
            case 'month':
                newRange = { from: startOfMonth(now), to: endOfDay(now) };
                break;
            case 'year':
                newRange = { from: startOfYear(now), to: endOfDay(now) };
                break;
            case 'custom':
                newRange = customRange;
                break;
            default:
                break;
        }

        setDateRange(newRange);
    };

    // Effect to refetch when dateRange changes (except for custom type where we wait for manual refresh or valid range update if we wanted auto)
    // But to align with SalesReport, we might want explicit refresh or auto. 
    // Let's do auto-fetch when dateRange updates, typically handled by calling fetch in handleFilterChange or useEffect.
    // Using useEffect on dateRange is cleaner but be careful with custom range partial updates.
    useEffect(() => {
        if (filterType !== 'custom') {
            fetchStaffSales();
        }
    }, [dateRange, selectedStaff]);

    // Handle custom date changes
    const handleCustomDateChange = (prop, value) => {
        if (!isValid(value)) return;

        let adjustedValue = value;
        if (prop === 'to') {
            adjustedValue = endOfDay(value);
        } else if (prop === 'from') {
            adjustedValue = startOfDay(value);
        }

        const newCustomRange = { ...customRange, [prop]: adjustedValue };
        setCustomRange(newCustomRange);

        if (newCustomRange.from && newCustomRange.to) {
            if (newCustomRange.from > newCustomRange.to) {
                setError('Start date cannot be after end date');
                return;
            }

            const diff = differenceInYears(newCustomRange.to, newCustomRange.from);
            if (diff >= 1) {
                setError('Date range cannot exceed 1 year');
            } else {
                setError('');
                setDateRange(newCustomRange);
            }
        }
    };

    // Calculate Summaries
    const totalRevenue = staffData.reduce((sum, staff) => sum + (parseFloat(staff.total_revenue) || 0), 0);
    const totalServices = staffData.reduce((sum, staff) => sum + (parseInt(staff.services_count) || 0), 0);
    const activeStaffCount = staffData.length;

    // Prepare Chart Data
    const getChartData = () => {
        if (!staffData.length) return [];

        return staffData
            .map(staff => ({
                name: `${staff.first_name} ${staff.last_name}`,
                revenue: parseFloat(staff.total_revenue) || 0,
                services: parseInt(staff.services_count) || 0,
                invoices: parseInt(staff.invoices_count) || 0
            }))
            .sort((a, b) => b.revenue - a.revenue); // Sort by revenue descending
    };

    const chartData = getChartData();

    if (loading) return <Loading message="Loading sales by staff..." />;

    return (
        <FeatureGuard feature="Reports & Analytics">
            <Box p={3}>
                {/* Header */}
                <Box display="flex" alignItems="center" mb={3}>
                    <People sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" fontWeight="bold">
                            Sales by Staff
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Performance breakdown by stylist
                        </Typography>
                    </Box>
                </Box>

                {/* Filters Card */}
                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={7}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Time Period
                                </Typography>
                                <ButtonGroup variant="outlined" aria-label="time period filters">
                                    <Button
                                        onClick={() => handleFilterChange('today')}
                                        variant={filterType === 'today' ? 'contained' : 'outlined'}
                                    >
                                        Today
                                    </Button>
                                    <Button
                                        onClick={() => handleFilterChange('week')}
                                        variant={filterType === 'week' ? 'contained' : 'outlined'}
                                    >
                                        This Week
                                    </Button>
                                    <Button
                                        onClick={() => handleFilterChange('month')}
                                        variant={filterType === 'month' ? 'contained' : 'outlined'}
                                    >
                                        This Month
                                    </Button>
                                    <Button
                                        onClick={() => handleFilterChange('year')}
                                        variant={filterType === 'year' ? 'contained' : 'outlined'}
                                    >
                                        This Year
                                    </Button>
                                    <Button
                                        onClick={() => handleFilterChange('custom')}
                                        variant={filterType === 'custom' ? 'contained' : 'outlined'}
                                    >
                                        Custom
                                    </Button>
                                </ButtonGroup>
                            </Grid>

                            {filterType === 'custom' ? (
                                <>
                                    <Grid item xs={12} md={5}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Custom Range
                                        </Typography>
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <Box display="flex" gap={2} alignItems="flex-start">
                                                <DatePicker
                                                    label="From"
                                                    value={customRange.from}
                                                    onChange={(newValue) => handleCustomDateChange('from', newValue)}
                                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                                />
                                                <DatePicker
                                                    label="To"
                                                    value={customRange.to}
                                                    onChange={(newValue) => handleCustomDateChange('to', newValue)}
                                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                                />
                                            </Box>
                                        </LocalizationProvider>
                                    </Grid>
                                </>
                            ) : (
                                <></>
                            )}
    
<Grid item xs={12} md={3}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Filter by Staff
                                        </Typography>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="staff-select-custom-label">Staff</InputLabel>
                                            <Select
                                                labelId="staff-select-custom-label"
                                                value={selectedStaff}
                                                label="Staff"
                                                onChange={(e) => setSelectedStaff(e.target.value)}
                                            >
                                                <MenuItem value="">
                                                    <em>All Staff</em>
                                                </MenuItem>
                                                {staffList.map((staff) => (
                                                    <MenuItem key={staff.id} value={staff.id}>
                                                        {staff.first_name} {staff.last_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                        </Grid>

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                                Reporting Period: <strong>{format(dateRange.from, 'PP')}</strong> - <strong>{format(dateRange.to, 'PP')}</strong>
                            </Typography>
                            <Button variant="outlined" startIcon={<Search />} onClick={fetchStaffSales} size="small">
                                Refresh Data
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                {/* Summary Cards */}
                <Grid container spacing={3} mb={4}>
                    <Grid item xs={12} md={4}>
                        <StatCard
                            title="Total Revenue"
                            value={formatPrice(totalRevenue)}
                            icon={<AttachMoney />}
                            color="primary"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <StatCard
                            title="Total Services"
                            value={totalServices}
                            icon={<ContentCut />}
                            color="info"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <StatCard
                            title="Active Staff"
                            value={activeStaffCount}
                            icon={<People />}
                            color="success"
                        />
                    </Grid>
                </Grid>

                {/* Revenue by Staff Chart */}
                {chartData.length > 0 && (
                    <Card sx={{ mb: 4 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Revenue by Staff
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    layout="vertical"
                                    data={chartData}
                                    margin={{
                                        top: 20,
                                        right: 30,
                                        left: 100,
                                        bottom: 20,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        type="number"
                                        tickFormatter={(value) => formatPrice(value)}
                                    />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        width={90}
                                    />
                                    <Tooltip
                                        formatter={(value) => formatPrice(value)}
                                        labelStyle={{ color: '#000' }}
                                    />
                                    <Legend />
                                    <Bar
                                        dataKey="revenue"
                                        fill="#1976d2"
                                        name="Revenue"
                                        radius={[0, 8, 8, 0]}
                                        maxBarSize={60}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                )}

                {/* Data Table */}
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Staff Name</TableCell>
                                <TableCell align="center">Services Performed</TableCell>
                                <TableCell align="center">Invoices Handled</TableCell>
                                <TableCell align="right">Revenue Generated</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {staffData.length > 0 ? (
                                staffData.map((staff) => (
                                    <TableRow key={staff.staff_id}>
                                        <TableCell>
                                            <Box display="flex" alignItems="center">
                                                <Typography fontWeight="medium">
                                                    {staff.first_name} {staff.last_name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">{staff.services_count}</TableCell>
                                        <TableCell align="center">{staff.invoices_count}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                            {formatPrice(staff.total_revenue)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <Box py={3}>
                                            <Typography color="text.secondary">
                                                No sales data found for this period.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Box>
        </FeatureGuard>
    );
};

export default SalesByStaff;
